import type { FC } from 'react';
import React from 'react';
import { matchPath } from 'react-router-dom';

import { InputTitle } from 'components/input/InputTitle';
import { Button, RadiusConfig, Spacing, TextInput, Textarea, Tooltip, Typography } from 'enable-ui';

import { ThumbnailComp } from 'modules/classes/components/ThumbnailComp';
import { Courses } from 'modules/dashboard/core/type';
import { BorderContainer } from 'modules/dashboard/styles/common';
import {
  DropdownBody,
  DropdownContent,
  Item,
  ItemContainer,
} from 'modules/dashboard/styles/dashboard';

import { ReactComponent as AddIcon } from 'assets/icons/add-icon.svg';
import { ReactComponent as CloseIcon } from 'assets/icons/close.svg';
import { ReactComponent as SearchIcon } from 'assets/icons/search.svg';

import { useBoolean } from 'hooks';

import { COLOR, COLOR_CONFIG } from 'constants/color';
import { PATH } from 'constants/path';

export const ClassesForm: FC<{
  name: string;
  description?: string;
  handleChange: any;
  selectedCourse?: Courses;
  onDeleteSelectedCourse?: () => void;
  searchValue?: string;
  onChangeSearchValue?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  coursesData?: Courses[];
  handleSelectedCourse?: (value: Courses) => void;
  file?: string;
  onChangeFiles?: (values: File[]) => void;
  onDeleteThumbnail?: () => void;
  handleGetWidthAndHeightThumbnail?: (e: any) => void;
}> = ({
  name,
  coursesData,
  searchValue,
  description,
  handleChange,
  selectedCourse,
  onChangeSearchValue,
  handleSelectedCourse,
  onDeleteSelectedCourse,
  file,
  onChangeFiles,
  onDeleteThumbnail,
  handleGetWidthAndHeightThumbnail,
}) => {
  const visible = useBoolean(false);

  const isEditPage = matchPath(
    `${PATH.CLASSES}/${PATH.CLASSES_DETAIL}/${PATH.CLASSES_EDIT}`,
    location.pathname,
  );

  const Wrapper = isEditPage ? React.Fragment : BorderContainer;

  return (
    <Wrapper className={file ? 'pb-0' : ''}>
      <Spacing>
        {!isEditPage ? (
          <Typography variant="h4" weight={500} color={COLOR.surfaceNeutralBold['neutral-11']}>
            Class details
          </Typography>
        ) : (
          <></>
        )}

        <InputTitle title="Class name" extraClass="mt-24" />
        <TextInput
          value={name}
          name="name"
          onChange={handleChange}
          containerStyle={{ maxWidth: 800 }}
        />
      </Spacing>

      <Spacing className="mt-24">
        <InputTitle title="Description" />
        <Textarea
          maxLength={200}
          name="description"
          value={description}
          onChange={handleChange}
          style={{
            padding: '6px 10px',
            background: COLOR_CONFIG.white,
          }}
          containerStyle={{
            maxWidth: '800px',
          }}
        />
      </Spacing>

      {!isEditPage && (
        <Spacing className="mt-24">
          <InputTitle title="Course" />
          {selectedCourse ? (
            <div
              className="max-w-800"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                border: `1px solid #00000026`,
                padding: '13px 16px',
                borderRadius: RadiusConfig.sm,
              }}
            >
              <div>
                <Typography variant="body-2" color={COLOR.surfaceNeutralBold['neutral-11']}>
                  {selectedCourse.name}
                </Typography>
                <Typography className="mt-2" variant="body-3" color={COLOR_CONFIG['neutral-text']}>
                  {selectedCourse.totalLessons} lessons
                </Typography>
              </div>

              <CloseIcon
                style={{ cursor: 'pointer' }}
                onClick={() => {
                  visible.setValue(false);
                  onDeleteSelectedCourse?.();
                }}
              />
            </div>
          ) : (
            <Tooltip
              bg="transparent"
              trigger="click"
              arrow={false}
              placement="bottom-start"
              offset={[5, 5]}
              content={
                <DropdownContent style={{ minWidth: 520, maxHeight: 512, overflow: 'auto' }}>
                  <TextInput
                    placeholder={'Search by course'}
                    prefix={<SearchIcon />}
                    value={searchValue}
                    onChange={onChangeSearchValue}
                  />
                  <DropdownBody>
                    {coursesData?.length ? (
                      coursesData?.map((el, index) => (
                        <ItemContainer>
                          <Item key={index} onClick={() => handleSelectedCourse?.(el)}>
                            {el.name}
                          </Item>
                        </ItemContainer>
                      ))
                    ) : (
                      <Typography style={{ textAlign: 'center' }}>No data</Typography>
                    )}
                  </DropdownBody>
                </DropdownContent>
              }
              visible={visible.value}
              allowHTML
              interactive
              onClickOutside={() => visible.setValue(false)}
            >
              <div>
                <Button
                  placementIcon="left"
                  icon={<AddIcon style={{ marginRight: 4 }} />}
                  style={{ background: '#fff' }}
                  onClick={() => visible.setValue((prev) => !prev)}
                >
                  Select course
                </Button>
              </div>
            </Tooltip>
          )}
        </Spacing>
      )}

      {!isEditPage && (
        <ThumbnailComp
          extraClass="mt-24 max-w-800"
          file={file as string}
          onChangeFiles={onChangeFiles as (value: File[]) => void}
          onDeleteThumbnail={onDeleteThumbnail as () => void}
          handleGetWidthAndHeightThumbnail={handleGetWidthAndHeightThumbnail as (e: any) => void}
        />
      )}
    </Wrapper>
  );
};
